import { useCallback } from 'react';
import { ConfigProvider } from 'antd';
import { useFela } from 'react-fela';
import { FormattedMessage } from 'react-intl';

import { Alert, Empty, SortIcon, Table } from '@/modules/ui';

import { useVaultsTableStore } from '../../hooks';
import type { VaultsTablePage, VaultsTableRow } from '../../types';
import { mapAntdSorterToVaultsTableSort } from '../../utils';
import { useVaultsTableColumns } from './hooks/useVaultsTableColumns';
import * as felaRules from './VaultsTable.rules';

export interface VaultsTableProps extends VaultsTablePage {
    isLoading: boolean;
    isError: boolean;
}

export const VaultsTable = ({ rows, total, isLoading, isError }: VaultsTableProps) => {
    const { css } = useFela();

    const { pagination, setPagination, setSort, resetSort } = useVaultsTableStore();
    const columns = useVaultsTableColumns();

    const handlePageChange = useCallback(
        (page: number, pageSize: number) => {
            setPagination({ page, limit: pageSize });
        },
        [setPagination],
    );

    return (
        <div className={css(felaRules.container)}>
            <ConfigProvider
                renderEmpty={() =>
                    isError ? (
                        <Alert
                            type='error'
                            title={<FormattedMessage id='vaults.table.loadingError' />}
                            customStyle={felaRules.errorAlert}
                        />
                    ) : (
                        <Empty text={<FormattedMessage id='vaults.table.empty' />} />
                    )
                }
            >
                <Table<VaultsTableRow>
                    className={css(felaRules.table)}
                    columns={columns.map(column => ({
                        ...column,
                        showSorterTooltip: false,
                        sortIcon: ({ sortOrder }) => <SortIcon sortOrder={sortOrder} />,
                    }))}
                    dataSource={rows}
                    // Keyed by id rather than by spreading a `key` into each row, so a row handed to a row
                    // action is the plain `Vault` the API returned.
                    rowKey='id'
                    // TODO: Restore alongside the bulk action bar once a bulk vault endpoint exists —
                    // until then the checkboxes select rows nothing can act on.
                    // rowSelection={{
                    //     preserveSelectedRowKeys: true,
                    //     columnWidth: '2.5rem',
                    //     selectedRowKeys: selectedRows,
                    //     onChange: setSelectedRows,
                    // }}
                    pagination={{
                        pageSize: pagination.limit,
                        current: pagination.page,
                        total,
                        onChange: handlePageChange,
                        showSizeChanger: true,
                    }}
                    onChange={(_pagination, _filters, sorter) => {
                        if (!('field' in sorter)) {
                            return;
                        }

                        const sort = mapAntdSorterToVaultsTableSort(sorter);

                        if (sort) {
                            setSort(sort);
                        } else {
                            resetSort();
                        }
                    }}
                    loading={isLoading}
                />
            </ConfigProvider>
        </div>
    );
};
